import { TFunction } from 'i18next'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { BaseModalType } from '@context/ModalsContext'
import { useProductReadContext } from '@context/ProductContext'

import { Modal } from '@ui/components/Modal'
import { RadioBoxProps } from '@ui/components/Modal/Body/CardSelect'
import { MT4Icon } from '@ui/icons/platforms/MT4.icon'
import { MT5Icon } from '@ui/icons/platforms/MT5.icon'
import { TMTIcon } from '@ui/icons/platforms/TMT.icon'
import { TradingViewIcon } from '@ui/icons/platforms/TradingView.icon'
import { IconFCType } from '@ui/icons/types'

import { NameDto } from '@model/NameDto'
import { PlatformTypeEnum } from '@model/PlatformTypeEnum'
import { TradingAccountInfo, TradingAccountInfoDetail } from '@model/TradingAccountInfo'

interface TradingPlatformsDataProps {
  tradingAccountsAccountInfo: TradingAccountInfo[]
  demo?: boolean
}

const useTradingPlatformsData = (props: TradingPlatformsDataProps) => {
  const { tradingAccountsAccountInfo, demo } = props
  const allowedPlatforms = useMemo(
    () =>
      demo
        ? tradingAccountsAccountInfo?.filter((x) => x.allowToOpenDemoAccount) ?? []
        : tradingAccountsAccountInfo ?? [],
    [tradingAccountsAccountInfo, demo]
  )
  return { allowedPlatforms }
}

const addPlatformDetails = (
  accountInfo: TradingAccountInfo,
  t: TFunction
): TradingAccountInfoDetail | null => {
  switch (accountInfo.platform.id) {
    case PlatformTypeEnum.MT4:
      return {
        ...accountInfo,
        hint: t('Trading Account.mt4.hint'),
        description: t('Trading Account.mt4.description'),
        logo: MT4Icon,
      }

    case PlatformTypeEnum.MT5:
      return {
        ...accountInfo,
        hint: t('Trading Account.mt5.hint'),
        description: t('Trading Account.mt5.description'),
        logo: MT5Icon,
      }

    case PlatformTypeEnum.TradingView:
      return {
        ...accountInfo,
        hint: t('Trading Account.tradingView.hint'),
        description: t('Trading Account.tradingView.description'),
        logo: TradingViewIcon,
      }

    case PlatformTypeEnum.TickmillTrader:
      return {
        ...accountInfo,
        hint: t('Trading Account.tickmillTrader.hint'),
        description: t('Trading Account.tickmillTrader.description'),
        logo: TMTIcon,
      }

    default:
      return null
  }
}

const getPlatformTypeName = (platformTypeId: PlatformTypeEnum, t: TFunction) => {
  switch (platformTypeId) {
    case PlatformTypeEnum.MT4:
      return t('Trading Account.mt4.name')
    case PlatformTypeEnum.MT5:
      return t('Trading Account.mt5.name')
    case PlatformTypeEnum.TradingView:
      return t('Trading Account.tradingView.name')
    case PlatformTypeEnum.CQGCAST:
      return t('Trading Account.cqgcast.name')
    case PlatformTypeEnum.TickmillTrader:
      return t('Trading Account.tickmillTrader.name')
    default:
      return ''
  }
}

interface Props {
  tradingAccountsAccountInfo: TradingAccountInfo[]
  demo?: boolean
  initialValue?: string | number
  onConfirm: (platform: NameDto) => void
}

export const TradingPlatformModal: BaseModalType<Props> = (props) => {
  const { close, tradingAccountsAccountInfo, demo, initialValue, onConfirm } = props
  const { t } = useTranslation()

  const { allowedPlatforms } = useTradingPlatformsData({ tradingAccountsAccountInfo, demo })
  const product = useProductReadContext()
  const platformOptions = useMemo(() => {
    return allowedPlatforms
      .filter((p) => p.product.id === product.product)
      .map((platformInfo) => {
        const details = addPlatformDetails(platformInfo, t)
        if (!details) return null
        return {
          id: details.platform.id,
          label: getPlatformTypeName(details.platform.id, t),
          logo: details.logo,
          hint: details.hint,
          hintColor: 'text-purple-high-em',
          description: details.description,
          isOnFire: details.platform.id === PlatformTypeEnum.TradingView,
        } satisfies Omit<RadioBoxProps, 'onClick' | 'checked' | 'value'> & {
          id: string | number
          logo: IconFCType<{}> | undefined
          description?: string
          isOnFire?: boolean
        }
      })
      .filter((x) => x !== null)
  }, [allowedPlatforms, product.product, t])
  const handleSelection = (selectedId: string) => {
    const selected = allowedPlatforms.find((p) => p.platform.id.toString() === selectedId)
    if (selected) {
      onConfirm(selected.platform)
    }
    close()
  }

  return (
    <Modal.FullScreen>
      <Modal.Header>{t('Trading Account.Platform Type')}</Modal.Header>
      <Modal.Body>
        <Modal.Body.CardSelect
          value={initialValue}
          onChange={handleSelection}
          options={platformOptions}
        />
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Secondary onClick={close}>{t('Cancel')}</Modal.Footer.Secondary>
      </Modal.Footer>
    </Modal.FullScreen>
  )
}
